import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatWithThousandSeparator } from '@/utils';
import { SectionHeader } from './SectionHeader';

interface ProductStatus {
  title: string;
  slug: string;
}

interface Product {
  title?: string;
  quantity?: number;
  price: number | string;
  status?: ProductStatus;
}

interface ItemSectionProps {
  products: Product[];
  currency: string;
}

const ITEM_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  new: { bg: 'rgba(168,85,247,0.12)', text: 'rgb(147,51,234)' },
  delivered: { bg: 'rgba(34,197,94,0.12)', text: 'rgb(22,163,74)' },
  returned: { bg: 'rgba(239,68,68,0.12)', text: 'rgb(220,38,38)' },
  damaged: { bg: 'rgba(245,158,11,0.12)', text: 'rgb(180,83,9)' },
  lost: { bg: 'rgba(107,114,128,0.12)', text: 'rgb(75,85,99)' },
};

export function ItemSection({ products, currency }: ItemSectionProps) {
  const { t } = useTranslation();

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card p-5'>
      <SectionHeader icon={Package} title={String(t('item'))} />

      {products.length === 0 ? (
        <p className='text-sm text-sd-muted-foreground'>—</p>
      ) : (
        <div className='flex flex-col gap-2'>
          {products.map((product, index) => {
            const displayName = product.title
              ? product.quantity && product.quantity > 1
                ? `${product.title} ×${product.quantity}`
                : product.title
              : '—';
            const displayPrice = `${formatWithThousandSeparator(product.price)} ${currency}`;
            const statusSlug = product.status?.slug;
            const statusColors = statusSlug
              ? ITEM_STATUS_COLORS[statusSlug] ?? { bg: 'rgba(107,114,128,0.12)', text: 'rgb(75,85,99)' }
              : null;

            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className='flex items-center justify-between rounded-[0.50rem] bg-sd-muted/40 px-4 py-3'
              >
                <span className='text-sm font-medium text-sd-foreground'>{displayName}</span>
                <div className='flex items-center gap-2'>
                  {statusColors && statusSlug && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: 11,
                        fontWeight: 600,
                        color: statusColors.text,
                        backgroundColor: statusColors.bg,
                        padding: '3px 8px',
                        borderRadius: 9999,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {String(t(`item_status.${statusSlug}`, { defaultValue: product.status?.title ?? statusSlug }))}
                    </span>
                  )}
                  <span className='text-sm font-semibold text-sd-foreground'>{displayPrice}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
